import React, { useEffect, useState } from "react";
import {
  Box,
  TextField,
  Typography,
  Button,
  TableCell,
  TableRow,
  Paper,
  Table,
  TableBody,
  TableContainer,
  CircularProgress,
  AlertColor,
} from "@mui/material";
import { getJwtTokenFromIdToken } from "../auth/AuthFanc";

interface BuildingEditFormProps {
  building: {
    buildingId: string;
    buildingManagementId: string;
    buildingName: string;
    buildingAddress: string;
    officeName: string;
    memo: string;
  };
  onMemoChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  buildingMemo: string;
  autoBuildingId: string;
  setSnackbarOpen: React.Dispatch<React.SetStateAction<boolean>>;
  setAlertSeverity: React.Dispatch<React.SetStateAction<AlertColor>>;
  setSnackbarMessage: React.Dispatch<React.SetStateAction<string>>;
}

const BuildingEditForm: React.FC<BuildingEditFormProps> = ({
  building,
  onMemoChange,
  autoBuildingId,
  buildingMemo,
  setSnackbarOpen,
  setAlertSeverity,
  setSnackbarMessage,
}) => {
  const CHARACTER_LIMIT = 50; // 文字数制限を定義

  const [isTouched, setIsTouched] = useState({
    buildingMemo: false,
  });

  const [validationErrors, setValidationErrors] = useState({
    buildingMemo: "",
  });

  const [saveLoading, setSaveLoading] = useState(false); // 保存ボタンのローディング状態

  const handleBlur = (field: string) => {
    setIsTouched({
      ...isTouched,
      [field]: true,
    });
  };

  useEffect(() => {
    const errors = {
      buildingMemo:
        isTouched.buildingMemo &&
        buildingMemo !== "" &&
        buildingMemo.length > CHARACTER_LIMIT
          ? `メモは${CHARACTER_LIMIT}文字以内で入力してください。`
          : "",
    };

    setValidationErrors(errors);
  }, [isTouched, buildingMemo]);

  const handleBuildingFormSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (Object.values(validationErrors).some((error) => error !== "")) {
      setSnackbarMessage("入力エラーがあります。修正してください。");
      setAlertSeverity("error");
      setSnackbarOpen(true);
      return;
    }

    setSaveLoading(true); // ローディング開始

    const jwtToken = await getJwtTokenFromIdToken();
    const url =
      process.env.REACT_APP_API_ENDPOINT + `/buildings/${autoBuildingId}`;

    try {
      const response = await fetch(url, {
        method: "PATCH",
        headers: {
          "Content-Type": "application/json",
          Authorization: jwtToken,
        },
        body: JSON.stringify({ memo: buildingMemo }),
      });

      if (!response.ok) {
        throw new Error("Failed to update memo");
      }

      const data = await response.json();
      console.log("物件情報更新APIのレスポンス" + JSON.stringify(data));
      setSnackbarMessage("更新完了しました");
      setAlertSeverity("success");
      setSnackbarOpen(true);
    } catch (err) {
      console.error(err);
      setSnackbarMessage("更新に失敗しました。");
      setAlertSeverity("error");
      setSnackbarOpen(true);
    } finally {
      setSaveLoading(false); // ローディング終了
    }
  };

  return (
    <>
      <TableContainer component={Paper}>
        <Table>
          <TableBody>
            <TableRow>
              <TableCell component="th" scope="row" colSpan={2}>
                <Typography variant="h6">物件情報</Typography>
              </TableCell>
            </TableRow>
            <TableRow>
              <TableCell>物件システムID</TableCell>
              <TableCell>{building.buildingId}</TableCell>
            </TableRow>
            <TableRow>
              <TableCell>取付先コード（物件管理ID）</TableCell>
              <TableCell>{building.buildingManagementId}</TableCell>
            </TableRow>
            <TableRow>
              <TableCell>取付先名（物件名称）</TableCell>
              <TableCell>{building.buildingName}</TableCell>
            </TableRow>
            <TableRow>
              <TableCell>物件住所</TableCell>
              <TableCell>{building.buildingAddress}</TableCell>
            </TableRow>
            <TableRow>
              <TableCell>管轄拠点</TableCell>
              <TableCell>{building.officeName}</TableCell>
            </TableRow>
            <TableRow>
              <TableCell>メモ</TableCell>
              <TableCell>
                <TextField
                  label="メモ"
                  multiline
                  rows={2}
                  value={buildingMemo}
                  onChange={onMemoChange}
                  sx={{ width: "600px" }}
                  onBlur={() => handleBlur("buildingMemo")}
                  helperText={validationErrors.buildingMemo}
                  error={validationErrors.buildingMemo !== ""}
                />
              </TableCell>
            </TableRow>
          </TableBody>
        </Table>
      </TableContainer>

      <Box display="flex" justifyContent="flex-end" marginTop={2}>
        <Button
          type="submit"
          variant="outlined"
          color="primary"
          onClick={handleBuildingFormSubmit}
          disabled={saveLoading}
        >
          {saveLoading ? <CircularProgress size={24} /> : "保存"}
        </Button>
      </Box>
    </>
  );
};

export default BuildingEditForm;
